import { useIntl } from "react-intl";
import { Link } from "react-router";
import { useParams } from "react-router-dom";
import { StyledButton } from "~Common/Components";
import PermissionCheck from "~Common/PermissionCheck";
import { DataTableImageColumn } from "~components/DataTable/components/DataTableImageColumn/DataTableImageColumn.tsx";
import { DataTableStringColumn } from "~components/DataTable/components/DataTableStringColumn/DataTableStringColumn.tsx";
import type { DataTableColumn } from "~components/DataTable/types/dataTableColumn.ts";
import type { DataTableRow } from "~components/DataTable/types/dataTableRow.ts";
import { ImageTypes } from "~enums/imageTypes.ts";
import { PermissionNames } from "~enums/permissions.ts";
import { ConferenceTableColumns } from "~pages/Conferences/ConferenceListPage/enums/conferenceTableColumns.ts";
import { useConferenceService } from "~pages/Conferences/hooks/useConferenceService.ts";
import type { UpdateConferenceModel } from "~pages/Conferences/types/updateConferenceModel.ts";
import type { Conference } from "~types/conference.ts";

export const useGetConferenceTableColumns = (massEditMode: boolean): Array<DataTableColumn<Conference>> => {
  const { formatMessage } = useIntl();
  const { sport, organizationId, customerId } = useParams();
  const { updateConference } = useConferenceService();

  const updateConferenceField = async (row: Conference & DataTableRow, updateData: Omit<UpdateConferenceModel, "conferenceId">) => {
    row.disabled = true;
    try {
      await updateConference(row.conferenceId, updateData);
    } finally {
      row.disabled = false;
    }
  };

  return [
    {
      key: ConferenceTableColumns.Images,
      title: formatMessage({ id: "image" }),
      dataIndex: "images",
      align: "center",
      width: 80,
      render: (images) => <DataTableImageColumn images={images} imageTypes={[ImageTypes.Logo]} />,
    },
    {
      key: ConferenceTableColumns.NameLocal,
      title: formatMessage({ id: "name.local.full" }),
      dataIndex: "nameLocal",
      ellipsis: { showTitle: true },
      sorter: true,
      render: (value: string, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          required
          onSave={(nameLocal) => updateConferenceField(row, { nameLocal })}
        />
      ),
    },
    {
      key: ConferenceTableColumns.NameLatin,
      title: formatMessage({ id: "name.latin.full" }),
      dataIndex: "nameLatin",
      ellipsis: { showTitle: true },
      sorter: true,
      render: (value: string | null, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          onSave={(nameLatin) => updateConferenceField(row, { nameLatin: nameLatin || null })}
        />
      ),
    },
    {
      key: ConferenceTableColumns.AbbreviationLocal,
      title: formatMessage({ id: "abbreviationLocal" }),
      dataIndex: "abbreviationLocal",
      width: 140,
      render: (value: string | null, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          onSave={(abbreviationLocal) => updateConferenceField(row, { abbreviationLocal: abbreviationLocal || null })}
        />
      ),
    },
    {
      key: ConferenceTableColumns.AbbreviationLatin,
      title: formatMessage({ id: "abbreviationLatin" }),
      dataIndex: "abbreviationLatin",
      width: 140,
      render: (value: string | null, row) => (
        <DataTableStringColumn
          value={value}
          isEditMode={massEditMode}
          disabled={row.disabled}
          onSave={(abbreviationLatin) => updateConferenceField(row, { abbreviationLatin: abbreviationLatin || null })}
        />
      ),
    },
    {
      key: ConferenceTableColumns.ConferenceId,
      title: formatMessage({ id: "conference.id" }),
      dataIndex: "conferenceId",
      copyable: false,
      width: 320,
    },
    {
      key: ConferenceTableColumns.ExternalId,
      title: formatMessage({ id: "externalId" }),
      dataIndex: "externalId",
      copyable: false,
      width: 220,
    },
    {
      align: "right",
      persistent: true,
      fixed: "right",
      width: 50,
      disableRowNavigation: true,
      render: (_value: unknown, row) => (
        <PermissionCheck permissionKeys={[PermissionNames.DIVISION]}>
          <Link to={`/${customerId}/admin/${sport}/${organizationId}/conferences/${row.conferenceId}/divisions`}>
            <StyledButton type={"text"}>{formatMessage({ id: "divisions" })}</StyledButton>
          </Link>
        </PermissionCheck>
      ),
    },
  ];
};
